/* Blessed Sips — interaction layer.
   No dependencies. Every behaviour degrades to working static HTML if this
   file fails to load: the photo track is a native scroll container, the size
   boxes are real buttons, and every link is a real href. */

(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- Scroll reveal ---------------------------------------------------
     Cards fade up once, staggered by their position within the grid row.
     Unobserved after firing, so scrolling back up stays quiet. */

  function initReveal() {
    var items = document.querySelectorAll('[data-bs-reveal]:not([data-bs-observed])');
    if (!items.length) return;

    if (reduced || !('IntersectionObserver' in window)) {
      items.forEach(function (el) {
        el.dataset.bsObserved = '1';
        el.classList.add('is-revealed', 'is-settled');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          var index = Number(el.dataset.bsIndex || 0);
          // Stagger across a row, then restart — a long grid should not end
          // up with a two-second delay on its last card.
          el.style.setProperty('--bs-delay', (index % 6) * 55 + 'ms');
          el.classList.add('is-revealed');
          el.addEventListener(
            'transitionend',
            function () {
              el.classList.add('is-settled');
            },
            { once: true }
          );
          observer.unobserve(el);
        });
      },
      { rootMargin: '0px 0px -6% 0px', threshold: 0.05 }
    );

    items.forEach(function (el) {
      el.dataset.bsObserved = '1';
      observer.observe(el);
    });
  }

  /* ---- Marquee ---------------------------------------------------------
     The track is duplicated at runtime so the -100% loop is seamless without
     authoring the copy twice in the template. */

  function initMarquee() {
    document.querySelectorAll('.bs-marquee').forEach(function (marquee) {
      var track = marquee.querySelector('.bs-marquee__track');
      if (!track || track.dataset.bsCloned) return;
      var clone = track.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.dataset.bsCloned = 'true';
      track.dataset.bsCloned = 'true';
      marquee.appendChild(clone);
    });
  }

  /* ---- Click particles -------------------------------------------------
     Vanilla port of the particle-button effect. Particles are appended to the
     button itself so they follow it on scroll, are aria-hidden, and clean
     themselves up on animationend rather than on a timer that could leak if
     the element is removed mid-flight. */

  function burst(button) {
    if (reduced) return;

    var COUNT = 6;
    for (var i = 0; i < COUNT; i++) {
      var p = document.createElement('span');
      p.className = 'bs-burst' + (i % 2 ? ' bs-burst--acid' : '');
      p.setAttribute('aria-hidden', 'true');

      // Fan upward and outward, alternating sides, same as the original.
      var dir = i % 2 ? 1 : -1;
      var x = dir * (18 + Math.random() * 26);
      var y = -(16 + Math.random() * 26);
      p.style.setProperty('--bs-burst-x', x.toFixed(1) + 'px');
      p.style.setProperty('--bs-burst-y', y.toFixed(1) + 'px');
      p.style.setProperty('--bs-burst-delay', i * 22 + 'ms');

      p.addEventListener('animationend', function () {
        if (this.parentNode) this.parentNode.removeChild(this);
      });

      button.appendChild(p);
    }
  }

  function pressFeedback(button) {
    button.classList.add('is-pressing');
    setTimeout(function () {
      button.classList.remove('is-pressing');
    }, 130);
  }

  /* ---- Product cards ---------------------------------------------------
     Three jobs: keep the dots in sync with the swipe, move the track when a
     size is chosen, and carry that choice into the product links. */

  function initCards() {
    document.querySelectorAll('[data-bs-card]:not([data-bs-wired])').forEach(function (card) {
      card.dataset.bsWired = '1';

      var track = card.querySelector('[data-bs-track]');
      var dots = card.querySelectorAll('[data-bs-dots] .bs-card__dot');
      var priceEl = card.querySelector('[data-bs-price]');
      var titleLink = card.querySelector('[data-bs-title-link]');
      var slideLinks = card.querySelectorAll('[data-bs-slide-link]');

      /* Dots follow the scroll position. rAF-throttled so a fast flick does
         not run this on every scroll event. */
      if (track && dots.length) {
        var frame = null;
        var current = 0;

        var sync = function () {
          frame = null;
          var slide = track.clientWidth || 1;
          var index = Math.round(track.scrollLeft / slide);
          index = Math.max(0, Math.min(index, dots.length - 1));
          if (index === current) return;
          current = index;
          dots.forEach(function (dot, i) {
            dot.classList.toggle('is-active', i === current);
          });
        };

        track.addEventListener(
          'scroll',
          function () {
            if (frame) return;
            frame = requestAnimationFrame(sync);
          },
          { passive: true }
        );
      }

      /* Option groups. A card can carry TWO groups (colour and size), so
         selection state is tracked per group — otherwise choosing a size
         would clear the chosen colour. */
      var groups = card.querySelectorAll('[data-bs-sizes]');
      groups.forEach(function (group) {
        var buttons = group.querySelectorAll('[data-bs-size]');
        buttons.forEach(function (button) {
          button.addEventListener('click', function () {
            if (button.getAttribute('aria-disabled') === 'true') return;

            var alreadyOn = button.getAttribute('aria-pressed') === 'true';

            // Only this group's buttons are deselected.
            buttons.forEach(function (other) {
              other.setAttribute('aria-pressed', other === button ? 'true' : 'false');
            });

            pressFeedback(button);
            if (!alreadyOn) burst(button);

            if (track) {
              var index = Number(button.dataset.slide || 0);
              var slides = track.children;
              if (slides[index]) {
                // Direct assignment, not scrollTo({behavior:'smooth'}):
                // mandatory scroll-snap cancels the scripted animation and
                // springs the track back to slide 0. Smoothing comes from
                // `scroll-behavior` in CSS, which snap does not fight.
                track.scrollLeft = slides[index].offsetLeft - track.offsetLeft;
              }
            }

            if (priceEl && button.dataset.price) {
              var compare = button.dataset.compare;
              priceEl.innerHTML = compare
                ? '<span class="bs-visually-hidden">Regular price</span>' +
                  '<span class="bs-card__price--was">' + compare + '</span>' +
                  '<span class="bs-visually-hidden">Sale price</span>' +
                  '<span class="bs-card__price--sale">' + button.dataset.price + '</span>'
                : '<span class="bs-visually-hidden">Price</span>' + button.dataset.price;
            }

            var url = button.dataset.url;
            if (url) {
              if (titleLink) titleLink.setAttribute('href', url);
              slideLinks.forEach(function (link) {
                link.setAttribute('href', url);
              });
            }
          });
        });
      });
    });
  }

  /* ---- Product page gallery -------------------------------------------- */

  function initGallery() {
    document.querySelectorAll('.bs-gallery:not([data-bs-wired])').forEach(function (gallery) {
      gallery.dataset.bsWired = '1';
      var viewport = gallery.querySelector('.bs-gallery__viewport');
      var slides = gallery.querySelectorAll('.bs-gallery__slide');
      var dots = gallery.querySelectorAll('.bs-gallery__dot');
      var prev = gallery.querySelector('[data-bs-prev]');
      var next = gallery.querySelector('[data-bs-next]');
      if (!viewport || slides.length < 2) return;

      var current = 0;

      function goTo(index) {
        current = Math.max(0, Math.min(index, slides.length - 1));
        viewport.scrollLeft = slides[current].offsetLeft - viewport.offsetLeft;
      }

      function sync() {
        var index = Math.round(viewport.scrollLeft / (viewport.clientWidth || 1));
        if (index === current) return;
        current = index;
        dots.forEach(function (dot, i) {
          dot.setAttribute('aria-current', i === current ? 'true' : 'false');
        });
      }

      var frame;
      viewport.addEventListener(
        'scroll',
        function () {
          if (frame) cancelAnimationFrame(frame);
          frame = requestAnimationFrame(sync);
        },
        { passive: true }
      );

      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () {
          goTo(i);
        });
      });
      if (prev) prev.addEventListener('click', function () { goTo(current - 1); });
      if (next) next.addEventListener('click', function () { goTo(current + 1); });
    });
  }

  /* ---- Product page variant options ------------------------------------ */

  function initOptions() {
    document.querySelectorAll('.bs-options:not([data-bs-wired])').forEach(function (group) {
      group.dataset.bsWired = '1';
      group.addEventListener('click', function (event) {
        var button = event.target.closest('.bs-option');
        if (!button || button.classList.contains('bs-option--unavailable')) return;
        group.querySelectorAll('.bs-option').forEach(function (other) {
          other.setAttribute('aria-pressed', other === button ? 'true' : 'false');
        });
      });
    });
  }

  /* ---- Accordion -------------------------------------------------------- */

  function initAccordion() {
    document.querySelectorAll('.bs-acc__trigger:not([data-bs-wired])').forEach(function (trigger) {
      trigger.dataset.bsWired = '1';
      trigger.addEventListener('click', function () {
        var expanded = trigger.getAttribute('aria-expanded') === 'true';
        var panel = document.getElementById(trigger.getAttribute('aria-controls'));
        trigger.setAttribute('aria-expanded', String(!expanded));
        if (panel) panel.hidden = expanded;
      });
    });
  }

  /* Picks a random product for the spotlight on every page load. Liquid cannot
     do this — Shopify caches the rendered HTML, so a Liquid-side pick would
     stay frozen until the cache turned over.

     The swap runs before the image is fetched: the img is lazy and below the
     fold, so changing src here costs nothing extra. */
  function initSpotlight() {
    document
      .querySelectorAll('.bs-spotlight:not([data-bs-wired])')
      .forEach(function (section) {
        section.setAttribute('data-bs-wired', '');

        var payload = section.querySelector('[data-bs-spotlight-pool]');
        var links = section.querySelectorAll('[data-bs-spotlight-link]');
        if (!payload || !links.length) return;

        var pool;
        try {
          pool = JSON.parse(payload.textContent);
        } catch (e) {
          return; /* Malformed payload: keep the server-rendered product. */
        }
        if (!Array.isArray(pool) || pool.length < 2) return;

        /* Draw without replacement. The two slots sit side by side, so picking
           each one independently would sooner or later show the same mug twice
           in the same row — which looks like a bug, not a coincidence. */
        var bag = pool.slice();
        for (var i = bag.length - 1; i > 0; i--) {
          var j = Math.floor(Math.random() * (i + 1));
          var swap = bag[i];
          bag[i] = bag[j];
          bag[j] = swap;
        }

        links.forEach(function (link, index) {
          var pick = bag[index];
          var img = link.querySelector('[data-bs-spotlight-img]');
          if (!pick || !pick.img || !pick.url || !img) return;
          link.setAttribute('href', pick.url);
          img.setAttribute('alt', pick.alt || '');
          img.setAttribute('src', pick.img);
        });
      });
  }

  /* Hover advances the card to the next photo, and leaving puts it back.

     "Back" means back to whatever was showing BEFORE the hover — not slide 0.
     A customer who picked a colour has a specific photo on screen, and
     resetting to the first image on mouse-out would silently undo that
     selection every time the pointer crossed the card.

     Pointer-only: on touch the same element is a swipe surface, and a tap
     that also jumped the photo would fight the gesture. */
  function initHoverSwap() {
    if (!window.matchMedia || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    document
      .querySelectorAll('[data-bs-card]:not([data-bs-hoverwired])')
      .forEach(function (card) {
        card.setAttribute('data-bs-hoverwired', '');

        var track = card.querySelector('[data-bs-track]');
        if (!track || track.children.length < 2) return;

        var restoreTo = null;

        function slideTo(index) {
          var slide = track.children[index];
          if (slide) track.scrollLeft = slide.offsetLeft - track.offsetLeft;
        }

        function currentIndex() {
          var w = track.clientWidth || 1;
          return Math.round(track.scrollLeft / w);
        }

        card.addEventListener('mouseenter', function () {
          restoreTo = currentIndex();
          slideTo((restoreTo + 1) % track.children.length);
        });

        card.addEventListener('mouseleave', function () {
          if (restoreTo !== null) slideTo(restoreTo);
          restoreTo = null;
        });
      });
  }

  /* ---- Wishlist -----------------------------------------------------------
     Saved in localStorage under one key. Every read is defensive: storage can
     be full, disabled (Safari private browsing throws on write), or hold junk
     from an older version of this code, and none of those may take the page
     down with them. */
  var WISH_KEY = 'bs-wishlist-v1';

  function wishRead() {
    try {
      var raw = window.localStorage.getItem(WISH_KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list.filter(function (i) { return i && i.handle; }) : [];
    } catch (e) {
      return [];
    }
  }

  function wishWrite(list) {
    try {
      window.localStorage.setItem(WISH_KEY, JSON.stringify(list));
      return true;
    } catch (e) {
      return false; /* Quota or private mode: the UI simply will not stick. */
    }
  }

  function wishCount() {
    var n = wishRead().length;
    document.querySelectorAll('[data-bs-wish-count]').forEach(function (el) {
      el.textContent = String(n);
      el.hidden = n === 0;
    });
  }

  function initWishlist() {
    document
      .querySelectorAll('[data-bs-wish]:not([data-bs-wired])')
      .forEach(function (button) {
        button.setAttribute('data-bs-wired', '');
        var handle = button.dataset.handle;
        if (!handle) return;

        var label = button.querySelector('[data-bs-wish-label]');

        function paint(saved) {
          button.setAttribute('aria-pressed', saved ? 'true' : 'false');
          button.classList.toggle('is-saved', saved);
          if (label) label.textContent = saved ? 'Saved to wishlist' : 'Add to wishlist';
        }

        paint(
          wishRead().some(function (i) { return i.handle === handle; })
        );

        button.addEventListener('click', function () {
          var list = wishRead();
          var at = -1;
          list.forEach(function (i, idx) { if (i.handle === handle) at = idx; });

          if (at > -1) {
            list.splice(at, 1);
          } else {
            list.push({
              handle: handle,
              title: button.dataset.title || handle,
              url: button.dataset.url || '/products/' + handle,
              img: button.dataset.img || '',
              price: button.dataset.price || '',
              variant: wishSelectedVariant()
            });
          }

          if (!wishWrite(list)) {
            if (label) label.textContent = 'Could not save — storage is blocked';
            return;
          }

          paint(at === -1);
          if (at === -1) { burst(button); flyToHeart(button); }
          paintHeart();
          pressFeedback(button);
          wishCount();
        });
      });

    wishCount();
    renderWishlistPage();
  }

  /* Product JSON is fetched once per handle and reused. A wishlist of a dozen
     mugs would otherwise hit the same endpoint on every re-render. */
  /* The variant showing on the product page at the moment they hit save.
     Shopify keeps it in the add-to-cart form's hidden id field, so reading it
     there gets the size and colour they were actually looking at rather than
     whatever happens to be first in the list. */
  function wishSelectedVariant() {
    var field = document.querySelector('form[action*="/cart/add"] [name="id"]');
    return field && field.value ? String(field.value) : '';
  }

  var wishProductCache = {};

  function wishProduct(handle) {
    if (wishProductCache[handle]) return wishProductCache[handle];
    wishProductCache[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js', {
      headers: { Accept: 'application/json' }
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
    return wishProductCache[handle];
  }

  /* Move to cart.

     The wishlist stores a handle, not a variant, so the variant has to be
     resolved at click time — and resolving it late is the right call anyway:
     the size someone saved a month ago may have sold out since.

     Where a product has more than one live variant we do NOT guess. Picking
     "the first available" would quietly drop an 11 oz in the cart for someone
     who wanted the 15 oz, and they would not notice until it arrived. Those
     go to the product page to choose. */
  function wishMoveToCart(item, button) {
    var original = button.textContent;
    button.disabled = true;
    button.textContent = 'Moving…';

    wishProduct(item.handle).then(function (data) {
      if (!data || !data.variants || !data.variants.length) {
        /* No product JSON means the handle is gone or the network failed.
           Send them to the page rather than fail silently. */
        window.location.href = item.url;
        return;
      }

      var live = data.variants.filter(function (v) { return v.available; });

      if (!live.length) {
        button.disabled = true;
        button.textContent = 'Sold out';
        return;
      }

      /* Prefer the exact variant they saved. If that option has sold out
         since, fall back to whatever is still buyable rather than dead-end
         them — but the saved one wins whenever it can. */
      var want = live[0];
      if (item.variant) {
        live.forEach(function (v) {
          if (String(v.id) === String(item.variant)) want = v;
        });
      }

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: want.id, quantity: 1 }] })
      })
        .then(function (r) { return r.ok ? r.json() : Promise.reject(r); })
        .then(function () {
          button.textContent = 'Moved to cart ✓';

          /* "Move" means it leaves the wishlist. Done after the add succeeds,
             never before — a failed add that had already emptied the wishlist
             would lose the item entirely. */
          var next = wishRead().filter(function (i) { return i.handle !== item.handle; });
          wishWrite(next);

          /* Let the theme's own cart UI know something changed. */
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          document.dispatchEvent(new CustomEvent('cart:change', { bubbles: true }));

          setTimeout(function () {
            renderWishlistPage();
            wishCount();
            paintHeart();
          }, 700);
        })
        .catch(function () {
          button.disabled = false;
          button.textContent = 'Could not add — try the product page';
        });
    });
  }

  /* Live availability, checked against Shopify rather than invented.

     This is the only stock fact a wishlist page can state truthfully: the
     page is rendered from localStorage, so Liquid never sees these products
     and Shopify's product JSON does not expose inventory counts. Whether a
     variant is buyable right now, it does expose — so that is what is shown. */
  function wishPaintStock(item, el, button) {
    wishProduct(item.handle).then(function (data) {
      if (!data || !data.variants) return;
      var live = data.variants.filter(function (v) { return v.available; });

      if (!live.length) {
        el.textContent = 'Sold out';
        el.hidden = false;
        if (button) {
          button.disabled = true;
          button.textContent = 'Sold out';
        }
      } else if (live.length < data.variants.length) {
        /* Some options gone, some left — true, and useful to know before
           clicking through. */
        el.textContent = 'Some options sold out';
        el.hidden = false;
      }
    });
  }

  function renderWishlistPage() {
    var grid = document.querySelector('[data-bs-wishlist-grid]');
    var empty = document.querySelector('[data-bs-wishlist-empty]');
    if (!grid) return;

    var list = wishRead();
    if (!list.length) {
      grid.hidden = true;
      if (empty) empty.hidden = false;
      return;
    }
    if (empty) empty.hidden = true;
    grid.hidden = false;
    grid.textContent = '';

    list.forEach(function (item, index) {
      var li = document.createElement('li');
      var card = document.createElement('article');
      card.className = 'bs-card';
      card.setAttribute('data-bs-index', String(index));

      var media = document.createElement('div');
      media.className = 'bs-card__media';
      if (item.img) {
        var a = document.createElement('a');
        a.href = item.url;
        var img = document.createElement('img');
        /* Built with DOM calls, not innerHTML: the title and url came from
           storage, and storage is writable by anything else on this origin. */
        img.src = item.img;
        img.alt = item.title;
        img.loading = 'lazy';
        a.appendChild(img);
        media.appendChild(a);
      }

      var info = document.createElement('div');
      info.className = 'bs-card__info';
      var h3 = document.createElement('h3');
      h3.className = 'bs-card__title';
      var link = document.createElement('a');
      link.href = item.url;
      link.textContent = item.title;
      h3.appendChild(link);

      var priceRow = document.createElement('p');
      priceRow.className = 'bs-card__price';

      var price = document.createElement('span');
      price.className = 'bs-card__price--sale';
      price.textContent = item.price || '';
      priceRow.appendChild(price);

      var stock = document.createElement('span');
      stock.className = 'bs-wish__stock';
      stock.hidden = true;
      priceRow.appendChild(stock);

      var move = document.createElement('button');
      move.type = 'button';
      move.className = 'bs-wish__move';
      move.textContent = 'Move to cart';

      var remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'bs-wish__remove';
      remove.textContent = 'Remove';
      remove.addEventListener('click', function () {
        var next = wishRead().filter(function (i) { return i.handle !== item.handle; });
        wishWrite(next);
        renderWishlistPage();
        wishCount();
        paintHeart();
      });

      move.addEventListener('click', function () { wishMoveToCart(item, move); });

      var actions = document.createElement('div');
      actions.className = 'bs-wish__actions';
      actions.appendChild(move);
      actions.appendChild(remove);

      info.appendChild(h3);
      info.appendChild(priceRow);
      info.appendChild(actions);
      card.appendChild(media);
      card.appendChild(info);
      li.appendChild(card);
      grid.appendChild(li);

      wishPaintStock(item, stock, move);
    });

    placeCartWishlist();
  }

  /* Sticky buy island. It does NOT submit anything itself — it forwards the
     click to the page's real add-to-cart button, so there is exactly one path
     into the cart. A second form here would be a second thing that can break
     checkout, for no gain. */
  /* ------------------------------------------------------------------
     Floating add-to-cart bar — rebuilt.

     The old one dropped in from the TOP of the window on every screen size.
     Two problems with that: on a phone the top edge is the hardest place to
     reach one-handed and it lands on the sticky header, and on desktop a
     floating pill hovering over the masthead reads like an ad rather than
     part of the page.

     This is a bottom bar on every size — the thumb rests there on a phone,
     and on desktop it sits quietly along the bottom edge where it never
     covers content the reader is looking at. It carries the thumbnail, the
     product name and the live price so it works as an anchor rather than a
     bare button, and the CTA is the loudest thing in it.

     It appears only after the real button has scrolled off the TOP — never
     while the button is still below the fold, which would show it to someone
     who has not yet seen the real one.
     ------------------------------------------------------------------ */
  function initBuyIsland() {
    if (document.querySelector('.bs-dock')) return;

    var real =
      document.querySelector('.product-form__submit') ||
      document.querySelector('[name="add"]');
    if (!real) return;

    var titleEl = document.querySelector('.product-details h1, .product-title, h1');
    var imgEl = document.querySelector('.product-media-container img, media-gallery img');

    var dock = document.createElement('div');
    dock.className = 'bs-dock';
    dock.setAttribute('role', 'region');
    dock.setAttribute('aria-label', 'Add to cart');

    var inner = document.createElement('div');
    inner.className = 'bs-dock__inner';

    if (imgEl && (imgEl.currentSrc || imgEl.src)) {
      var thumb = document.createElement('img');
      thumb.className = 'bs-dock__thumb';
      thumb.src = imgEl.currentSrc || imgEl.src;
      thumb.alt = '';
      thumb.width = 44;
      thumb.height = 44;
      inner.appendChild(thumb);
    }

    var txt = document.createElement('span');
    txt.className = 'bs-dock__txt';

    var name = document.createElement('span');
    name.className = 'bs-dock__name';
    /* Same trim as the H1 — the SEO tail after the pipe would eat the bar. */
    name.textContent = ((titleEl && titleEl.textContent) || '').split('|')[0].trim();

    var price = document.createElement('span');
    price.className = 'bs-dock__price';

    /* Mirror whatever the real price element says, and keep mirroring it so a
       variant change is reflected here too. */
    function syncPrice() {
      var live = document.querySelector('.product-details .price-item--sale, .product-details .price-item, .price-item--sale');
      if (live) price.textContent = (live.textContent || '').trim();
    }
    syncPrice();

    txt.appendChild(name);
    txt.appendChild(price);
    inner.appendChild(txt);

    var cta = document.createElement('button');
    cta.type = 'button';
    cta.className = 'bs-dock__cta';
    cta.textContent = 'Add to cart';
    cta.addEventListener('click', function () {
      /* Click the real button rather than posting to the cart ourselves, so
         the theme's own validation, variant state and drawer all still run. */
      real.click();
    });
    inner.appendChild(cta);

    dock.appendChild(inner);
    document.body.appendChild(dock);

    var priceHost = document.querySelector('.product-details');
    if (priceHost && 'MutationObserver' in window) {
      var pending = null;
      new MutationObserver(function () {
        clearTimeout(pending);
        pending = setTimeout(syncPrice, 120);
      }).observe(priceHost, { childList: true, subtree: true, characterData: true });
    }

    if (!('IntersectionObserver' in window)) return;
    new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          /* boundingClientRect.top < 0 means it went off the top, i.e. the
             customer scrolled PAST it. Without that test the bar also shows
             while the real button is still below the fold. */
          var past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          dock.classList.toggle('is-up', past);
          document.body.classList.toggle('bs-dock-open', past);
        });
      },
      { threshold: 0 }
    ).observe(real);
  }

  /* Reviews drift to the right on their own. Pauses on hover, on focus and
     whenever the customer is scrolling it themselves, so it never fights the
     person reading it. Off entirely under reduced-motion. */
  function initReviewRail() {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    document
      .querySelectorAll('.gsr-list:not([data-bs-wired]), .gsr-rail:not([data-bs-wired])')
      .forEach(function (rail) {
        rail.setAttribute('data-bs-wired', '');
        if (rail.scrollWidth <= rail.clientWidth + 4) return;

        var paused = false;
        var idleUntil = 0;

        ['mouseenter', 'focusin', 'touchstart', 'pointerdown'].forEach(function (evt) {
          rail.addEventListener(evt, function () { paused = true; }, { passive: true });
        });
        ['mouseleave', 'focusout', 'touchend', 'pointerup'].forEach(function (evt) {
          rail.addEventListener(evt, function () { paused = false; }, { passive: true });
        });
        rail.addEventListener('wheel', function () { idleUntil = Date.now() + 2000; }, { passive: true });

        setInterval(function () {
          if (paused || Date.now() < idleUntil || document.hidden) return;
          var max = rail.scrollWidth - rail.clientWidth;
          rail.scrollLeft = rail.scrollLeft >= max - 1 ? 0 : rail.scrollLeft + 1;
        }, 30);
      });
  }


  /* Clamp the description and add a Read more toggle — but only when the text
     is actually taller than the clamp. A button that expands nothing is worse
     than no button. */
  function initReadMore() {
    document.querySelectorAll('[data-bs-readmore]:not([data-bs-wired])').forEach(function (wrap) {
      wrap.setAttribute('data-bs-wired', '');
      var body = wrap.querySelector('[data-bs-readmore-body]');
      if (!body) return;

      wrap.classList.add('is-clamped');
      if (body.scrollHeight <= body.clientHeight + 4) {
        wrap.classList.remove('is-clamped');
        return;
      }

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bs-desc__more';
      btn.textContent = 'Read more';
      btn.setAttribute('aria-expanded', 'false');
      btn.addEventListener('click', function () {
        var open = wrap.classList.toggle('is-open');
        wrap.classList.toggle('is-clamped', !open);
        btn.textContent = open ? 'Read less' : 'Read more';
        btn.setAttribute('aria-expanded', String(open));
      });
      wrap.appendChild(btn);
    });
  }

  /* Prev/next for the review rail. Steps by one card width so a click always
     lands on a card edge rather than halfway through one. */
  function initReviewNav() {
    document.querySelectorAll('.gsr-railwrap:not([data-bs-wired])').forEach(function (wrap) {
      wrap.setAttribute('data-bs-wired', '');
      var rail = wrap.querySelector('.gsr-list');
      var prev = wrap.querySelector('[data-bs-rev-prev]');
      var next = wrap.querySelector('[data-bs-rev-next]');
      if (!rail || !prev || !next) return;

      function step(dir) {
        var card = rail.children[0];
        var by = card ? card.getBoundingClientRect().width + 16 : rail.clientWidth * 0.8;
        rail.scrollLeft += dir * by;
      }
      prev.addEventListener('click', function () { step(-1); });
      next.addEventListener('click', function () { step(1); });
    });
  }


  /* ---- Spin to win --------------------------------------------------------
     The result is decided by weighted draw FIRST, then the wheel is animated
     so it lands on that slice. Doing it the other way — spin freely and read
     where it stopped — cannot honour configured odds. */
  var SPIN_KEY = 'bs-spin-v1';

  function initSpin() {
    var root = document.querySelector('[data-bs-spin]:not([data-bs-wired])');
    if (!root) return;
    root.setAttribute('data-bs-wired', '');
    if (root.dataset.enabled === '0') return;

    /* Asked once. A popup that returns on every page load is the single most
       complained-about pattern in ecommerce. */
    try {
      if (window.localStorage.getItem(SPIN_KEY)) return;
    } catch (e) {}

    var wheel = root.querySelector('[data-bs-spin-wheel]');
    var go = root.querySelector('[data-bs-spin-go]');
    var form = root.querySelector('[data-bs-spin-form]');
    var won = root.querySelector('[data-bs-spin-won]');
    var result = root.querySelector('[data-bs-spin-result]');
    var err = root.querySelector('[data-bs-spin-err]');
    if (!wheel || !go || !form || !won) return;

    var labels = (root.dataset.labels || '').split(',').map(function (t) { return t.trim(); });
    var weights = (root.dataset.weights || '').split(',').map(function (t) {
      var n = parseFloat(t);
      return isFinite(n) && n > 0 ? n : 0;
    });
    while (weights.length < labels.length) weights.push(0);

    function mark(state) {
      try { window.localStorage.setItem(SPIN_KEY, state); } catch (e) {}
    }

    function close() {
      root.hidden = true;
      document.documentElement.style.overflow = '';
      mark('closed');
    }

    root.querySelectorAll('[data-bs-spin-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !root.hidden) close();
    });

    var delay = Number(root.dataset.delay || 30) * 1000;
    setTimeout(function () {
      try { if (window.localStorage.getItem(SPIN_KEY)) return; } catch (e) {}
      root.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      go.focus();
    }, delay);

    var spun = false;
    go.addEventListener('click', function () {
      if (spun) return;
      spun = true;
      go.disabled = true;

      var total = weights.reduce(function (a, b) { return a + b; }, 0);
      var pick = 0;
      if (total > 0) {
        var roll = Math.random() * total;
        for (var i = 0; i < weights.length; i++) {
          roll -= weights[i];
          if (roll <= 0) { pick = i; break; }
        }
      }

      var per = 360 / labels.length;
      /* Land the middle of the chosen slice under the pin at the top, after
         five full turns so it reads as a real spin. */
      var target = 360 * 5 + (360 - (pick * per + per / 2));
      wheel.style.transition = reduced ? 'none' : 'transform 4200ms cubic-bezier(.15,.9,.2,1)';
      wheel.style.transform = 'rotate(' + target + 'deg)';

      setTimeout(function () {
        if (result) result.textContent = 'You won ' + labels[pick] + '!';
        go.hidden = true;
        form.hidden = false;
        var input = form.querySelector('input');
        if (input) input.focus();
      }, reduced ? 0 : 4300);
    });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var input = form.querySelector('input[type="email"]');
      var value = input ? input.value.trim() : '';
      /* Deliberately loose: the real check is Shopify's when they subscribe.
         Rejecting odd-but-valid addresses here just loses the customer. */
      if (!value || value.indexOf('@') < 1 || value.lastIndexOf('.') < value.indexOf('@')) {
        if (err) { err.textContent = 'Enter an email address so we can send your code.'; err.hidden = false; }
        if (input) input.focus();
        return;
      }
      if (err) err.hidden = true;
      form.hidden = true;
      won.hidden = false;
      mark('won');
    });

    var copy = root.querySelector('[data-bs-spin-copy]');
    var codeEl = root.querySelector('[data-bs-spin-code]');
    if (copy && codeEl) {
      copy.addEventListener('click', function () {
        var text = codeEl.textContent.trim();
        if (navigator.clipboard) {
          navigator.clipboard.writeText(text).then(function () {
            copy.textContent = 'Copied';
          });
        } else {
          copy.textContent = text;
        }
      });
    }
  }


  /* ---- Product page colour swatches ---------------------------------------
     Horizon only paints swatches when they are configured in Shopify admin.
     This fills the gap by matching the option label against the same colour
     table the homepage cards use, so "Sage" or "Navy" shows its colour with
     no admin setup.

     Deliberately additive: it sets a background on the existing label and
     never touches the radio, the form, or any name/value. The picker still
     drives the variant exactly as it did — a colour that cannot be resolved
     is simply left as a normal text button rather than painted grey. */
  var BS_COLOURS = {
    /* Added after 'Light Green' resolved to black on the live site:
       it was absent from the table, so nothing sane could be picked. */
    lightgreen: '#9ad9a0',
    darkgreen: '#1b4d2e',
    seagreen: '#2e8b57',
    limegreen: '#63c73b',
    lime: '#9fd356',
    jade: '#3aa07a',
    hunter: '#355e3b',
    moss: '#8a9a5b',
    armygreen: '#4b5320',
    babyblue: '#bfe3f5',
    powderblue: '#b0e0e6',
    turquoise: '#40c8c8',
    aqua: '#5fd6d6',
    hotpink: '#f0509a',
    magenta: '#c74ac7',
    khaki: '#c3b091',
    camel: '#c19a6b',
    wine: '#722f37',
    lightpink: '#f7d3dc',
    lightpurple: '#c9aee6',
    lightyellow: '#f8e59a',
    darkblue: '#16305c',
    darkgrey: '#4a4a4a',
    darkgray: '#4a4a4a',
    lightorange: '#f7b267',
    offblack: '#1a1a1a',
black:'#0b0b0b',white:'#ffffff',cream:'#f2efe6',ivory:'#fffff0',offwhite:'#f7f4ec',sand:'#e3d5b8',sandtan:'#d8c3a0',tan:'#d2b48c',beige:'#f5f5dc',natural:'#e8dcc8',pink:'#f4c2c2',blush:'#f0b8c0',blushpink:'#f0b8c0',rose:'#e0808e',peach:'#ffcba4',coral:'#f28374',red:'#d92121',crimson:'#b31217',burgundy:'#800020',maroon:'#7b1e3a',wine:'#722f37',orange:'#e8760f',rust:'#b7410e',terracotta:'#c1613b',clay:'#c98d7a',amber:'#ffbf00',yellow:'#f5d020',mustard:'#d4a017',gold:'#d4af37',honey:'#e6a817',green:'#2e7d32',forest:'#1b4d2e',forestgreen:'#1b4d2e',sage:'#5f7d6f',olive:'#6b7a3a',mint:'#98d8b0',emerald:'#0f7a4f',teal:'#1f7a7a',blue:'#2b6cb0',navy:'#1a2a5e',sky:'#93d5f2',skyblue:'#93d5f2',lightblue:'#a8d8f0',royalblue:'#2b4fb0',cobalt:'#0047ab',denim:'#4a6fa5',purple:'#6b3fa0',plum:'#8e4585',lavender:'#c5b3e6',lilac:'#c8a2c8',violet:'#7f4fc9',brown:'#6b4423',chocolate:'#5c3317',coffee:'#4b3621',espresso:'#3b2412',mocha:'#7d5a4f',taupe:'#8b8073',grey:'#8a8a8a',gray:'#8a8a8a',lightgrey:'#c4c4c4',lightgray:'#c4c4c4',charcoal:'#3a3a3a',slate:'#5a6570',silver:'#c0c0c0',stone:'#a8a29e',graphite:'#4a4a4a'};

  /* Reads the colour Shopify itself configured, when there is one. Covers
     names my table does not know — "Light Green" was rendering black because
     it is not in the list and nothing else supplied a colour. */
  function horizonSwatchColour(label) {
    var inner = label.querySelector('.variant-option__swatch, .variant-option__button-label__pill');
    if (!inner) return '';
    var v = inner.style.getPropertyValue('--swatch-background') ||
      inner.style.backgroundColor ||
      (window.getComputedStyle ? window.getComputedStyle(inner).backgroundColor : '');
    if (!v) return '';
    v = v.trim();
    /* Ignore transparent and fully-unset values, which are not colours. */
    if (v === 'transparent' || v.indexOf('rgba(0, 0, 0, 0)') === 0) return '';
    return v;
  }

  function initPdpSwatches() {
    document
      .querySelectorAll('.variant-option--buttons label.variant-option__button-label:not([data-bs-sw])')
      .forEach(function (label) {
        label.setAttribute('data-bs-sw', '');

        /* Only colour-ish option groups. A size button reading "Natural" is a
           real colour name, so the group has to be identified before painting.

           Two independent signals, because the first one alone was too
           fragile: Horizon does not always put the option name in a <legend>,
           and when it does not, every colour silently stayed unpainted.
             1. the group's own legend/label text says colour/shade/finish, OR
             2. most of the values in the group resolve to known colour names,
                which is what a colour group looks like from the outside. */
        var group = label.closest('fieldset, .variant-option');
        if (!group) return;

        var named = group.querySelector('legend, .variant-option__label, [class*="option-name"]');
        var byName = /colou?r|shade|finish/i.test(named ? named.textContent : '');

        var byContent = false;
        if (!byName) {
          var all = group.querySelectorAll('.variant-option__button-label');
          var hits = 0;
          all.forEach(function (l) {
            var n = (l.textContent || '').trim().toLowerCase().replace(/[\s_-]/g, '');
            if (BS_COLOURS[n]) hits++;
          });
          byContent = all.length > 0 && hits / all.length >= 0.6;
        }

        if (!byName && !byContent) return;

        var name = (label.textContent || '').trim().toLowerCase()
          .replace(/[\s_-]/g, '');
        var hex = BS_COLOURS[name];
        if (!hex) hex = horizonSwatchColour(label);
        if (!hex) {
          /* Unknown name and no configured swatch: give it the page colour and
             ink text. Anything is better than leaving it to inherit a dark
             background and render as unreadable text on black. */
          label.classList.add('bs-pdpsw', 'bs-pdpsw--unknown');
          label.style.setProperty('--bs-sw', '#f6f3ea');
          label.style.setProperty('--bs-sw-ink', '#0b0b0b');
          label.setAttribute('data-label', (label.textContent || '').trim());
          return;
        }



        label.classList.add('bs-pdpsw');
        label.style.setProperty('--bs-sw', hex);
        label.setAttribute('data-label', (label.textContent || '').trim());

        /* If Horizon rendered its own swatch span inside the label, paint
           that too. Left alone it kept its configured colour on a strip
           while the label showed ours — two colours on one chip. */
        var inner = label.querySelector('.variant-option__swatch, .variant-option__button-label__pill');
        if (inner) inner.style.background = hex;

        /* Pick the text colour from the swatch's own luminance so the name
           stays readable on both a cream and a near-black chip. Hard-coding
           one ink colour makes the label vanish on half the palette. */
        var r, g, b;
        if (hex.charAt(0) === '#') {
          r = parseInt(hex.slice(1, 3), 16);
          g = parseInt(hex.slice(3, 5), 16);
          b = parseInt(hex.slice(5, 7), 16);
        } else {
          /* rgb()/rgba() from a configured swatch. */
          var parts = hex.replace(/[^0-9,.]/g, '').split(',');
          r = Number(parts[0]) || 0;
          g = Number(parts[1]) || 0;
          b = Number(parts[2]) || 0;
        }
        var chan = [r, g, b].map(function (c) {
          c = c / 255;
          return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
        });
        var lum = 0.2126 * chan[0] + 0.7152 * chan[1] + 0.0722 * chan[2];
        /* Crossover between the two inks actually in use. The dark ink is
           #0b0b0b, not pure black, so its own luminance (0.0033) belongs in
           the equation: (L+0.05)/0.0533 = 1.05/(L+0.05) solves to L = 0.1866.
           Using the pure-black figure of 0.179 put mid-tones like sage on the
           wrong side and dropped them to 4.36:1. */
        label.style.setProperty('--bs-sw-ink', lum > 0.1866 ? '#0b0b0b' : '#ffffff');
      });
  }

  /* Horizon re-renders the variant picker when a variant changes, which throws
     away the inline colours painted onto the old labels — every chip came back
     black. A section-load listener does not cover it, because that swap is not
     a section load. Watching the picker for replaced children does. */
  function watchPdpSwatches() {
    var picker = document.querySelector('.variant-picker__form, variant-picker, .variant-option');
    if (!picker || picker.dataset.bsSwWatch) return;
    var host = picker.closest('.variant-picker__form') || picker.parentNode;
    if (!host) return;
    host.dataset.bsSwWatch = '1';

    var pending = false;
    new MutationObserver(function () {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () {
        pending = false;
        initPdpSwatches();
        initChosenState();
      });
    }).observe(host, { childList: true, subtree: true });
  }


  /* Bundle tiers: the acid highlight follows the quantity actually chosen,
     rather than sitting permanently on the "most popular" one. Reads the
     count from whatever the section marks as selected, so it keeps working
     if that markup is a class, a checked input or an aria state. */
  function initBundleTiers() {
    var wrap = document.querySelector('.gsb, .gsb-wrap');
    if (!wrap || wrap.dataset.bsTierWired) return;
    var tiers = wrap.querySelectorAll('.gsb-tier');
    if (!tiers.length) return;
    wrap.dataset.bsTierWired = '1';

    function chosenCount() {
      return wrap.querySelectorAll(
        '.gsb-item--current, .gsb-item.is-selected, .gsb-item[aria-selected="true"]'
      ).length || wrap.querySelectorAll('.gsb-item input:checked').length;
    }

    function tierNumber(tier) {
      var el = tier.querySelector('.gsb-tier-n') || tier;
      var m = (el.textContent || '').match(/\d+/);
      return m ? Number(m[0]) : 0;
    }

    function paint() {
      var count = chosenCount();
      /* Highest tier whose threshold the basket has reached. */
      var best = null;
      tiers.forEach(function (t) {
        var n = tierNumber(t);
        if (n && count >= n && (!best || n >= tierNumber(best))) best = t;
      });
      tiers.forEach(function (t) {
        var on = t === best;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-current', on ? 'true' : 'false');
      });
    }

    paint();
    wrap.addEventListener('click', function () { requestAnimationFrame(paint); });
    wrap.addEventListener('change', function () { requestAnimationFrame(paint); });
    new MutationObserver(function () { paint(); }).observe(wrap, {
      subtree: true, attributes: true, attributeFilter: ['class', 'aria-selected', 'checked']
    });
  }


  /* The radio lives INSIDE the label in Horizon, not as a sibling before it.
     Every selected-state rule was written as `input:checked + label`, which
     needs a previous sibling — so none of them ever matched and selection
     never showed. CSS now uses :has(); this mirrors the same state onto a
     class as well, so the styling does not depend on :has support alone. */
  function initChosenState() {
    var groups = document.querySelectorAll('.variant-option');
    if (!groups.length) return;

    function sync() {
      document.querySelectorAll('.variant-option__button-label').forEach(function (label) {
        var input = label.querySelector('input[type="radio"]');
        label.classList.toggle('is-chosen', !!(input && input.checked));
      });
    }

    sync();
    groups.forEach(function (g) {
      if (g.dataset.bsChosen) return;
      g.dataset.bsChosen = '1';
      g.addEventListener('change', sync);
      g.addEventListener('click', function () { requestAnimationFrame(sync); });
    });
  }


  /* ---- Wishlist heart in the header --------------------------------------
     Injected rather than edited into header.liquid: the header markup is
     Horizon's and changing it risks the nav. This appends one link, keeps a
     live count on it, and flies a heart to it when something is saved. */
  function initHeaderHeart() {
    if (document.querySelector('[data-bs-heart]')) return;
    /* Anchor to the cart or account link and sit beside it, rather than
       appending to the header element — that fell back to the header itself
       and the heart landed in a row of its own below the logo. */
    /* Anchor beside the cart, but NEVER give up: the previous version bailed
       out when no <a href="/cart"> existed, and Horizon renders the cart as a
       drawer trigger on some setups — so the heart vanished entirely. Each
       step falls through to a wider target, ending at the header itself. */
    var anchor =
      document.querySelector('a[href*="/cart"], a[href$="/cart"]') ||
      document.querySelector('[class*="header"] a[href*="cart"]') ||
      document.querySelector('[class*="cart"] a, a[class*="cart"], button[class*="cart"]') ||
      document.querySelector('a[href*="/account"], [class*="header"] a[href*="account"]');

    var host = null;
    if (anchor && anchor.parentNode) {
      host = anchor.parentNode;
    } else {
      host =
        document.querySelector('[class*="header__icons"], [class*="header-icons"], [class*="header"] [class*="icons"]') ||
        document.querySelector('.header, header.header') ||
        document.querySelector('header');
      anchor = null;
    }
    if (!host) return;

    var a = document.createElement('a');
    a.className = 'bs-heart';
    a.href = '/pages/my-wishlist';
    a.setAttribute('aria-label', 'My wishlist');
    a.setAttribute('data-bs-heart', '');
    a.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">' +
      '<path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1L12 21l7.7-7.6 1.1-1a5.5 5.5 0 0 0 0-7.8z"/></svg>' +
      '<span class="bs-heart__n" data-bs-heart-count hidden>0</span>';
    /* Before the cart when we found it, otherwise appended to whatever
       icon row we landed on. Either way it renders. */
    if (anchor) host.insertBefore(a, anchor); else host.appendChild(a);
    paintHeart();
  }

  function paintHeart() {
    var n = wishRead().length;
    document.querySelectorAll('[data-bs-heart-count]').forEach(function (el) {
      el.textContent = String(n);
      el.hidden = n === 0;
    });
    var heart = document.querySelector('[data-bs-heart]');
    if (heart) heart.classList.toggle('is-filled', n > 0);
  }

  /* A heart leaves the button and lands on the header icon. Purely additive:
     if the icon is missing or motion is reduced, nothing happens and the save
     still works. */
  function flyToHeart(from) {
    if (reduced || !from) return;
    var target = document.querySelector('[data-bs-heart]');
    if (!target) return;

    var a = from.getBoundingClientRect();
    var b = target.getBoundingClientRect();

    var fly = document.createElement('span');
    fly.className = 'bs-fly';
    fly.setAttribute('aria-hidden', 'true');
    fly.innerHTML =
      '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
      '<path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1L12 21l7.7-7.6 1.1-1a5.5 5.5 0 0 0 0-7.8z"/></svg>';
    fly.style.left = a.left + a.width / 2 + 'px';
    fly.style.top = a.top + a.height / 2 + 'px';
    fly.style.setProperty('--dx', b.left + b.width / 2 - (a.left + a.width / 2) + 'px');
    fly.style.setProperty('--dy', b.top + b.height / 2 - (a.top + a.height / 2) + 'px');
    document.body.appendChild(fly);

    fly.addEventListener('animationend', function () {
      if (fly.parentNode) fly.parentNode.removeChild(fly);
      target.classList.add('is-bump');
      setTimeout(function () { target.classList.remove('is-bump'); }, 380);
      paintHeart();
    });
  }


  /* ------------------------------------------------------------------
     Video stories rail.

     Plays a clip on hover or keyboard focus, pauses and rewinds on the way
     out — the behaviour of the React reference, without React.

     Three things the reference got right and are kept:
       - play() returns a promise that REJECTS if the browser refuses (a
         tab in the background, a data-saver setting, the element being
         removed mid-gesture). Unhandled, that prints an error to every
         visitor's console, so it is caught and ignored.
       - pausing also resets currentTime, so the next hover starts the clip
         from the top rather than resuming mid-sentence.
       - the start offset is applied on the way in, not parsed out of a URL
         hash — Shopify builds the video URL, so a #t= fragment is not ours
         to add.

     Reduced motion is honoured by never auto-playing. The poster frame
     stays, and the clip is still reachable by tapping through to the page.
     ------------------------------------------------------------------ */
  /* ------------------------------------------------------------------
     Story lightbox.

     Tapping a clip opens it large with sound. The rail itself stays muted
     because muted is the only way a browser will autoplay at all — but a
     click is a user gesture, and a gesture is exactly what unmutes are
     allowed to ride on. So sound is turned on here and nowhere else.

     The card stays a real <a> to the product underneath. That keeps
     middle-click, "open in new tab" and a no-JS visitor working, and it is
     why the dialog carries a product link of its own: opening the video
     should not cost someone the way to buy the thing in it.
     ------------------------------------------------------------------ */
  var vmodal = null;

  function vmodalBuild() {
    if (vmodal) return vmodal;

    var root = document.createElement('div');
    root.className = 'bs-vmodal';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', 'Video');
    root.hidden = true;

    var back = document.createElement('div');
    back.className = 'bs-vmodal__back';

    var panel = document.createElement('div');
    panel.className = 'bs-vmodal__panel';

    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'bs-vmodal__close';
    close.setAttribute('aria-label', 'Close video');
    close.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';

    var stage = document.createElement('div');
    stage.className = 'bs-vmodal__stage';

    var cta = document.createElement('a');
    cta.className = 'bs-vmodal__cta';
    cta.textContent = 'View product';

    panel.appendChild(close);
    panel.appendChild(stage);
    panel.appendChild(cta);
    root.appendChild(back);
    root.appendChild(panel);
    document.body.appendChild(root);

    back.addEventListener('click', vmodalClose);
    close.addEventListener('click', vmodalClose);

    /* Escape closes, and Tab is trapped between the only two controls so
       focus cannot wander behind the dialog. */
    root.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { vmodalClose(); return; }
      if (e.key !== 'Tab') return;
      var stops = [close, cta];
      var i = stops.indexOf(document.activeElement);
      e.preventDefault();
      var next = e.shiftKey ? i - 1 : i + 1;
      if (next < 0) next = stops.length - 1;
      if (next >= stops.length) next = 0;
      stops[next].focus();
    });

    vmodal = { root: root, stage: stage, close: close, cta: cta, opener: null };
    return vmodal;
  }

  function vmodalClose() {
    if (!vmodal || vmodal.root.hidden) return;
    var v = vmodal.stage.querySelector('video');
    if (v) { try { v.pause(); } catch (e) {} }
    vmodal.stage.textContent = '';
    vmodal.root.hidden = true;
    document.documentElement.classList.remove('bs-noscroll');
    /* Send focus back where it came from, or a keyboard user is dumped at
       the top of the document. */
    if (vmodal.opener) { try { vmodal.opener.focus(); } catch (e) {} }
    vmodal.opener = null;
  }

  function vmodalOpen(card) {
    var source = card.querySelector('video');
    if (!source) return;

    var m = vmodalBuild();
    m.opener = card;
    m.stage.textContent = '';

    /* Clone rather than move the rail's video: moving it would leave a hole
       in the carousel behind the dialog. The clone carries its own <source>
       children, so no URL has to be dug out and re-assembled. */
    var v = source.cloneNode(true);
    v.removeAttribute('class');
    v.className = 'bs-vmodal__video';
    v.muted = false;
    v.defaultMuted = false;
    v.removeAttribute('muted');
    v.controls = true;
    v.loop = false;               /* looping audio with no way out is hostile */
    v.setAttribute('playsinline', '');
    v.playsInline = true;
    v.preload = 'auto';

    m.stage.appendChild(v);

    var href = card.getAttribute('href');
    if (href) { m.cta.href = href; m.cta.hidden = false; }
    else { m.cta.hidden = true; }

    /* Stop the muted preview behind the dialog. */
    document.querySelectorAll('[data-bs-story]').forEach(function (c) {
      if (c.bsStop) c.bsStop();
    });

    m.root.hidden = false;
    document.documentElement.classList.add('bs-noscroll');

    try { v.load(); } catch (e) {}
    var start = parseFloat(card.getAttribute('data-start')) || 0;
    if (start) { try { v.currentTime = start; } catch (e) {} }

    var p = v.play();
    if (p && typeof p.catch === 'function') {
      p.catch(function () {
        /* Sound refused for some reason — fall back to muted so the visitor
           still sees the clip, and leave the controls for them to unmute. */
        v.muted = true;
        var again = v.play();
        if (again && typeof again.catch === 'function') again.catch(function () {});
      });
    }

    m.close.focus();
  }


  /* ------------------------------------------------------------------
     "(N% off)" beside the product price.

     Horizon owns the price markup and re-renders it on every variant
     change, so this reads the two figures that are already on the page,
     works out the saving and injects one span — then watches for the
     re-render and does it again. Nothing is invented: if there is no
     compare-at price, nothing is shown.
     ------------------------------------------------------------------ */
  function money(el) {
    if (!el) return NaN;
    var t = (el.textContent || '').replace(/[^0-9.,]/g, '').replace(/,/g, '');
    return parseFloat(t);
  }

  /* ------------------------------------------------------------------
     Two small corrections to the buy box.

     1. The visible H1 is the raw Shopify title, which carries an SEO tail
        after a pipe: "... Sister in Christ Mug | Ceramic Coffee Cup". That
        tail earns its keep in <title> and in search results; on the page it
        just makes the heading a line longer and reads like a listing rather
        than a product. Only the visible text is trimmed — document.title,
        the meta tags and the structured data are untouched.

     2. Every product here currently has one size. Horizon still renders the
        picker, so the visitor sees a control that cannot be operated. It is
        hidden only when there is genuinely nothing to choose; the capacity
        is shown as a spec in the essentials block instead.
     ------------------------------------------------------------------ */
  function initBuyBox() {
    var h1 = document.querySelector('.product-details h1');
    if (h1 && !h1.dataset.bsTrimmed) {
      var full = (h1.textContent || '').trim();
      var cut = full.split('|')[0].trim();
      /* Only when a tail actually exists and something substantial is left —
         never leave a heading shorter than a few words. */
      if (cut && cut.length > 12 && cut.length < full.length) {
        h1.textContent = cut;
        h1.setAttribute('title', full);
      }
      h1.dataset.bsTrimmed = '1';
    }

    document.querySelectorAll('variant-picker').forEach(function (vp) {
      var choices = vp.querySelectorAll('input[type="radio"], option');
      if (choices.length <= 1) vp.classList.add('bs-onevariant');
      else vp.classList.remove('bs-onevariant');
    });
  }

  function initPdpPrice() {
    document.querySelectorAll('.price:not([data-bs-off])').forEach(function (box) {
      var sale = box.querySelector('.price-item--sale, .price__sale .price-item');
      if (!sale) return;
      box.setAttribute('data-bs-off', '');

      var was = box.querySelector('.price-item--regular, s, del');
      var a = money(sale);
      var b = money(was);

      /* No real Compare-at price. Only derive one if the shop has explicitly
         switched that on — the setting ships off, and the label in the theme
         editor says why. */
      if (!was || !isFinite(b) || b <= a) {
        var flag = document.querySelector('[data-bs-refprice]');
        var mode = flag ? flag.getAttribute('data-bs-refprice') : 'off';
        if (mode !== 'double' && mode !== 'half_more') return;
        if (!isFinite(a) || a <= 0) return;

        var mult = mode === 'double' ? 2 : 1.5;
        var text = (sale.textContent || '').trim();
        var num = text.match(/[\d.,]+/);
        if (!num) return;

        /* Rebuild by swapping the number inside the original string, so the
           currency symbol and its position survive whatever locale is set. */
        var derived = (parseFloat(num[0].replace(/,/g, '')) * mult).toFixed(2);
        var span = document.createElement('span');
        span.className = 'price-item--regular bs-pdp-was';
        span.textContent = text.replace(num[0], derived);
        sale.insertAdjacentElement('beforebegin', span);
        was = span;
        b = parseFloat(derived);
      }

      if (!isFinite(a) || !isFinite(b) || b <= a) return;

      var pct = Math.round(((b - a) / b) * 100);
      if (pct <= 0) return;

      var tag = document.createElement('span');
      tag.className = 'bs-pdp-off';
      tag.textContent = '(' + pct + '% off)';
      was.insertAdjacentElement('afterend', tag);
    });
  }

  function watchPdpPrice() {
    var host = document.querySelector('.price');
    host = host && host.parentElement;
    if (!host || host.dataset.bsPriceWatch) return;
    host.dataset.bsPriceWatch = '1';
    new MutationObserver(function () {
      /* The re-render drops the injected span with the rest of the old
         markup, so the guard attribute goes with it and this re-runs. */
      initGalleryStart();
    hideDuplicatePrice();
    initPdpPrice();
    renderCartWishlist();
    }).observe(host, { childList: true, subtree: true });
  }

  /* ------------------------------------------------------------------
     "Forgot anything?" — the wishlist, offered once more on the cart.

     Image and an Add button, nothing else. The shopper already knows what
     these are; a second wall of titles and prices would compete with the
     cart itself, which is the thing they came here to finish.
     ------------------------------------------------------------------ */

  /* Where the flying image should land. Themes differ, so this walks a
     fallback chain and gives up gracefully to the top-right corner — an
     animation that flies somewhere sensible beats no animation. */
  function cartTarget() {
    return (
      document.querySelector('[data-bs-cart-icon]') ||
      document.querySelector('a[href$="/cart"], a[href*="/cart?"]') ||
      document.querySelector('[class*="header"] a[href*="cart"]') ||
      document.querySelector('[class*="cart-icon"], [class*="cart-drawer"] button, button[class*="cart"]') ||
      null
    );
  }

  function flyToCart(fromEl) {
    if (reduced || !fromEl) return;

    var a = fromEl.getBoundingClientRect();
    var target = cartTarget();
    var bx, by;
    if (target) {
      var b = target.getBoundingClientRect();
      bx = b.left + b.width / 2;
      by = b.top + b.height / 2;
    } else {
      bx = window.innerWidth - 40;
      by = 40;
    }

    /* The product image itself flies, not a generic icon — it reads as
       "this exact thing went into the bag". */
    var fly = document.createElement('img');
    fly.src = fromEl.currentSrc || fromEl.src;
    fly.className = 'bs-cartfly';
    fly.setAttribute('aria-hidden', 'true');
    fly.style.left = a.left + 'px';
    fly.style.top = a.top + 'px';
    fly.style.width = a.width + 'px';
    fly.style.height = a.height + 'px';
    fly.style.setProperty('--dx', bx - (a.left + a.width / 2) + 'px');
    fly.style.setProperty('--dy', by - (a.top + a.height / 2) + 'px');
    document.body.appendChild(fly);

    fly.addEventListener('animationend', function () { fly.remove(); });
    setTimeout(function () { if (fly.parentNode) fly.remove(); }, 1400);

    if (target) {
      target.classList.add('bs-cart-bump');
      setTimeout(function () { target.classList.remove('bs-cart-bump'); }, 700);
    }
  }

  function cartWishAdd(item, button, img) {
    button.disabled = true;
    button.textContent = 'Adding…';

    wishProduct(item.handle).then(function (data) {
      if (!data || !data.variants) {
        window.location.href = item.url;
        return;
      }
      var live = data.variants.filter(function (v) { return v.available; });
      if (!live.length) {
        button.textContent = 'Sold out';
        return;
      }

      /* Same rule as the wishlist page: the variant they saved wins, and
         only falls back if it has sold out since. */
      var want = live[0];
      if (item.variant) {
        live.forEach(function (v) { if (String(v.id) === String(item.variant)) want = v; });
      }

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: want.id, quantity: 1 }] })
      })
        .then(function (r) { return r.ok ? r.json() : Promise.reject(r); })
        .then(function () {
          flyToCart(img);
          button.textContent = 'Added ✓';
          button.classList.add('is-added');

          /* It is in the cart now, so it is no longer "forgotten". */
          var next = wishRead().filter(function (i) { return i.handle !== item.handle; });
          wishWrite(next);
          wishCount();
          paintHeart();

          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

          /* Let the animation land before the row disappears, then reload so
             the cart list above actually shows the new line. */
          setTimeout(function () { renderCartWishlist(); }, 900);
          setTimeout(function () { window.location.reload(); }, 1500);
        })
        .catch(function () {
          button.disabled = false;
          button.textContent = 'Try again';
        });
    });
  }


  /* Sit the strip directly above the discount field.

     The discount box is a static block inside the cart section, so no amount
     of section reordering in cart.json can reach it — the box has to be moved
     in the DOM. Shopify re-renders the summary on every quantity change, which
     puts the discount field back and leaves ours behind, so this re-runs
     whenever the cart updates. */
  function placeCartWishlist() {
    var box = document.querySelector('[data-bs-cartwish]');
    if (!box) return;

    /* An EMPTY cart renders no summary at all, so there is no discount field
       to sit above. The box stays where the template puts it — right under the
       cart — which is already the top of the page in that state. */
    var discount = document.querySelector('accordion-custom.cart-discount, .cart-discount');
    if (!discount || !discount.parentElement) {
      box.classList.remove('bs-cartwish--insummary');
      return;
    }

    if (discount.previousElementSibling === box) return;   /* already there */
    box.classList.add('bs-cartwish--insummary');
    discount.parentElement.insertBefore(box, discount);
  }

  /* Build the strip inside the cart drawer.

     The header cart icon is a button that opens <cart-drawer-component>, not
     a link to /cart — so the drawer is a completely separate DOM that a page
     section can never appear in. That is why the strip only showed after
     Bundle & Save: that flow lands on the full cart page. The drawer needs its
     own copy, created here. */
  function ensureDrawerWishlist() {
    var drawer = document.querySelector('cart-drawer-component');
    if (!drawer) return null;

    var existing = drawer.querySelector('[data-bs-cartwish]');
    if (existing) return existing;

    var box = document.createElement('section');
    box.className = 'bs-cartwish bs-cartwish--drawer';
    box.setAttribute('data-bs-cartwish', '');
    box.hidden = true;

    var h = document.createElement('h2');
    h.className = 'bs-cartwish__title';
    h.textContent = 'Forgot anything from your wishlist?';

    var rail = document.createElement('div');
    rail.className = 'bs-cartwish__rail';
    var track = document.createElement('ul');
    track.className = 'bs-cartwish__track';
    track.setAttribute('data-bs-cartwish-track', '');
    rail.appendChild(track);

    box.appendChild(h);
    box.appendChild(rail);

    /* Below the items, so it reads as an afterthought rather than pushing the
       cart contents out of view. */
    var host = drawer.querySelector('.cart-drawer__inner') || drawer;
    host.appendChild(box);
    return box;
  }

  function renderCartWishlist() {
    ensureDrawerWishlist();

    /* Every box on the page — the cart-page section AND the drawer copy.
       This used to be a single querySelector, which meant whichever one came
       first won and the other stayed empty. */
    var boxes = document.querySelectorAll('[data-bs-cartwish]');
    if (!boxes.length) return;

    var list = wishRead();

    boxes.forEach(function (box) {
      var track = box.querySelector('[data-bs-cartwish-track]');
      if (!track) return;

      if (!list.length) {
        box.hidden = true;
        /* Clear as well as hide: a hidden box holding the last render would
           hand stale tiles back if it were ever shown again. */
        track.textContent = '';
        return;
      }

      var wasHidden = box.hidden;
      box.hidden = false;
      track.textContent = '';

      if (wasHidden) {
        box.classList.remove('bs-cartwish--in');
        requestAnimationFrame(function () { box.classList.add('bs-cartwish--in'); });
      }

      list.forEach(function (item) {
        var li = document.createElement('li');
        li.className = 'bs-cartwish__item';

        var link = document.createElement('a');
        link.className = 'bs-cartwish__pic';
        link.href = item.url;
        link.setAttribute('aria-label', item.title);

        var img = document.createElement('img');
        /* DOM calls, not innerHTML: this came out of storage, and storage is
           writable by anything else on this origin. */
        img.src = item.img || '';
        img.alt = '';
        img.loading = 'lazy';
        link.appendChild(img);

        var add = document.createElement('button');
        add.type = 'button';
        add.className = 'bs-cartwish__add';
        add.textContent = 'Add';
        add.setAttribute('aria-label', 'Add ' + item.title + ' to cart');
        add.addEventListener('click', function () { cartWishAdd(item, add, img); });

        li.appendChild(link);
        li.appendChild(add);
        track.appendChild(li);
      });
    });

    placeCartWishlist();
  }

  /* The drawer is built and emptied by the theme as it opens and as the cart
     changes, which wipes anything added to it. Watch it and put the strip
     back. */
  function watchCartDrawer() {
    var drawer = document.querySelector('cart-drawer-component');
    if (!drawer || drawer.dataset.bsWatched) return;
    drawer.dataset.bsWatched = '1';

    var pending = null;
    new MutationObserver(function () {
      clearTimeout(pending);
      pending = setTimeout(function () {
        if (!drawer.querySelector('[data-bs-cartwish]')) renderCartWishlist();
      }, 150);
    }).observe(drawer, { childList: true, subtree: true, attributes: true, attributeFilter: ['open'] });
  }


  /* ------------------------------------------------------------------
     The gallery must open on the product's front image.

     Horizon jumps to the pre-selected variant's photo on load, so a mug
     whose first variant is Black opened on the black photo even when the
     merchant's featured image was the floral one. Measured live: Shopify
     reported featured "20260811005914-...png" while the gallery showed
     "13966089913418209820.jpg".

     Shopify puts the featured image first in product.media, so slide 0 is
     the right target. This runs ONCE on load — switching a colour after
     that should still move the gallery, and this must not fight it.
     ------------------------------------------------------------------ */
  function initGalleryStart() {
    /* A link that names a variant (?variant=123) is asking for that colour on
       purpose — usually from an ad or a shared link. Forcing the front image
       there would throw away what the visitor actually clicked. */
    if (/[?&]variant=/.test(window.location.search)) return;

    /* `media-gallery` and `slideshow-component` are custom ELEMENTS, so they
       need tag selectors. My first version used `.media-gallery`, a class that
       does not exist on this theme — it matched nothing and silently did
       nothing at all. */
    var gallery = document.querySelector('media-gallery, slideshow-component, .product-media-container');
    if (!gallery || gallery.dataset.bsFirst) return;
    gallery.dataset.bsFirst = '1';

    var dots = gallery.querySelectorAll('.slideshow-controls__dots li button');
    if (!dots.length) return;

    /* aria-selected is printed on the first dot by Liquid no matter which
       slide is actually showing, so it cannot be read as "already correct".
       My first version checked it and returned early every single time. */
    requestAnimationFrame(function () {
      try { dots[0].click(); } catch (e) {}
    });
  }

  /* The theme prints the price twice — a small one in .price__regular and
     the real one in .price-item__group. Measured live at 13px and 17px, same
     figure. The small one is noise, so it goes whenever the real one exists. */
  function hideDuplicatePrice() {
    var sale = document.querySelector('.price-item--sale');
    if (!sale) return;
    document.querySelectorAll('.price__regular').forEach(function (el) {
      if (el.contains(sale)) return;              /* never hide the real one */
      if (!/\d/.test(el.textContent)) return;
      el.style.display = 'none';
    });
  }

  /* Shopify replaces the summary markup on quantity changes; put the strip
     back above the discount field each time. */
  ['cart:refresh', 'cart:update', 'cart:change'].forEach(function (evt) {
    document.addEventListener(evt, function () {
      setTimeout(placeCartWishlist, 120);
    });
  });


  /* ------------------------------------------------------------------
     Shuffle a product grid on every page load.

     Liquid has no random filter and Shopify caches the rendered page, so
     the order has to be changed in the browser. Fisher-Yates, applied to
     the <li> elements before anything else runs, so the cards are already
     in their new order by the time the page paints.

     This reorders what the section rendered — it does not pull different
     products. Each strip still owns its own slice of the catalogue, which
     is what stops the same mug appearing in two sections at once.
     ------------------------------------------------------------------ */
  function initShuffle() {
    document.querySelectorAll('[data-bs-shuffle]:not([data-bs-shuffled])').forEach(function (grid) {
      grid.setAttribute('data-bs-shuffled', '');
      var items = [].slice.call(grid.children);
      if (items.length < 3) return;

      for (var i = items.length - 1; i > 0; i--) {
        var k = Math.floor(Math.random() * (i + 1));
        var t = items[i]; items[i] = items[k]; items[k] = t;
      }

      /* One fragment, one reflow — appending 12 items one by one would
         thrash layout on a phone. */
      var frag = document.createDocumentFragment();
      items.forEach(function (el) { frag.appendChild(el); });
      grid.appendChild(frag);
    });
  }

  function initStories() {
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* A phone on a metered plan should not be handed autoplaying video. */
    var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    var saveData = !!(conn && conn.saveData);

    /* The real split is not "phone vs desktop", it is whether a pointer can
       hover at all. A touch screen has no hover state to trigger playback,
       so on touch the rail plays the clip you are looking at instead. */
    var canHover = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    var autoOK = !reduce && !saveData;

    document.querySelectorAll('[data-bs-story]:not([data-bs-story-on])').forEach(function (card) {
      card.setAttribute('data-bs-story-on', '');
      var vid = card.querySelector('video');
      if (!vid) return;

      var start = parseFloat(card.getAttribute('data-start')) || 0;

      /* Muted and inline are set here as well as in Liquid. Without muted a
         browser refuses to start playback at all; without playsinline iOS
         throws the clip into its fullscreen player instead of playing it in
         the card. Both are required for the touch autoplay below to work. */
      vid.muted = true;
      vid.setAttribute('muted', '');
      vid.setAttribute('playsinline', '');
      vid.playsInline = true;

      card.bsPlay = function () {
        if (!autoOK) return;
        try { if (vid.currentTime < start) vid.currentTime = start; } catch (e) {}
        /* play() returns a promise that REJECTS when the browser declines —
           a backgrounded tab, a data-saver setting, the element removed
           mid-gesture. Unhandled, that logs an error for every visitor. */
        var p = vid.play();
        if (p && typeof p.catch === 'function') p.catch(function () {});
      };

      card.bsStop = function () {
        try {
          vid.pause();
          vid.currentTime = start;
        } catch (e) {}
      };

      /* Hover only where hovering exists. On touch these would either never
         fire or fire on the tap that is trying to follow the link. */
      if (canHover) {
        card.addEventListener('mouseenter', card.bsPlay);
        card.addEventListener('mouseleave', card.bsStop);
      }

      /* Keyboard works on every device. */
      card.addEventListener('focus', card.bsPlay);
      card.addEventListener('blur', card.bsStop);

      /* Open the clip with sound. Modified clicks are left alone so
         ctrl/cmd-click and middle-click still open the product in a new tab,
         which is what the href is there for. */
      card.addEventListener('click', function (e) {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;
        e.preventDefault();
        vmodalOpen(card);
      });
    });

    document.querySelectorAll('[data-bs-stories]:not([data-bs-stories-on])').forEach(function (wrap) {
      wrap.setAttribute('data-bs-stories-on', '');
      var track = wrap.querySelector('[data-bs-stories-track]');
      if (!track) return;

      var cards = [].slice.call(wrap.querySelectorAll('[data-bs-story]'));

      if ('IntersectionObserver' in window) {
        if (canHover) {
          /* Desktop: a clip scrolled away should not keep decoding frames. */
          var off = new IntersectionObserver(function (entries) {
            entries.forEach(function (e) {
              if (!e.isIntersecting && e.target.bsStop) e.target.bsStop();
            });
          }, { threshold: 0.1 });
          cards.forEach(function (c) { off.observe(c); });
        } else if (autoOK) {
          /* Touch: play the clip most on screen, and only that one. Playing
             every visible clip at once would decode three videos in parallel
             on the weakest hardware and burn the battery for two clips
             nobody is looking at.

             root stays the viewport on purpose: the observer already
             accounts for clipping by the rail's own horizontal scroll, so
             one observer covers both "scrolled past sideways" and "rail is
             below the fold". */
          var pick = new IntersectionObserver(function (entries) {
            entries.forEach(function (e) { e.target.bsRatio = e.intersectionRatio; });

            var best = null;
            var bestRatio = 0.5; /* must be at least half on screen to win */
            cards.forEach(function (c) {
              var r = c.bsRatio || 0;
              if (r > bestRatio) { bestRatio = r; best = c; }
            });

            cards.forEach(function (c) {
              if (c === best) { if (c.bsPlay) c.bsPlay(); }
              else if (c.bsStop) { c.bsStop(); }
            });
          }, { threshold: [0, 0.25, 0.5, 0.75, 1] });
          cards.forEach(function (c) { pick.observe(c); });
        }
      }

      function step(dir) {
        var item = track.querySelector('.bs-stories__item');
        /* Scroll by whole cards so a clip is never left half cut off. */
        var w = item ? item.getBoundingClientRect().width + 12 : track.clientWidth * 0.8;
        track.scrollBy({ left: dir * w * 2, behavior: reduce ? 'auto' : 'smooth' });
      }

      var prev = wrap.querySelector('[data-bs-stories-prev]');
      var next = wrap.querySelector('[data-bs-stories-next]');
      if (prev) prev.addEventListener('click', function () { step(-1); });
      if (next) next.addEventListener('click', function () { step(1); });

      /* Grey out an arrow that cannot go anywhere, so it never lies about
         there being more to see. */
      function sync() {
        var max = track.scrollWidth - track.clientWidth - 2;
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max;
      }
      track.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync);
      sync();
    });
  }

  function init() {
    initShuffle();
    initHeaderHeart();
    initChosenState();
    initBundleTiers();
    initPdpSwatches();
    watchPdpSwatches();
    initSpin();
    initReadMore();
    initReviewNav();
    initBuyIsland();
    initReviewRail();
    initWishlist();
    initHoverSwap();
    initReveal();
    initMarquee();
    initCards();
    initGallery();
    initOptions();
    initAccordion();
    initSpotlight();
    initStories();
    initBuyBox();
    initPdpPrice();
    watchPdpPrice();
    initGalleryStart();
    hideDuplicatePrice();
    renderCartWishlist();
    watchCartDrawer();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Shopify's theme editor re-renders sections without a page load. Every
     init above is guarded so re-running only wires what is new. */
  document.addEventListener('shopify:section:load', init);
})();
